import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, useParams, Navigate } from 'react-router-dom'
import i18n, { LANGUAGES } from './i18n'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import AdminRoute from './components/AdminRoute'
import HomePage from './pages/HomePage'
import KnowledgeProvider from './components/KnowledgeProvider'
import ScrollToTop from './components/ScrollToTop'
import SeznamRetargeting from './components/SeznamRetargeting'
import GoogleAnalytics from './components/GoogleAnalytics'
import { flushPendingSeznamConversion } from './lib/seznamRetargeting'
import { flushPendingGoogleAdsConversion } from './lib/googleAnalytics'
import VersionGuard from './components/VersionGuard'
import Toaster from './components/Toaster'
import IdleTimer from './components/IdleTimer'
import SessionEndedScreen from './components/SessionEndedScreen'

// Stránky lazy — kód routy se stáhne až při návštěvě → menší vstupní JS, lepší TTI/Lighthouse.
// HomePage zůstává eager (landing — bez loader-flashe, lepší LCP).
const LoginPage = lazy(() => import('./pages/LoginPage'))
const RegisterPage = lazy(() => import('./pages/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const AnalysisResultPage = lazy(() => import('./pages/AnalysisResultPage'))
const HistoryPage = lazy(() => import('./pages/HistoryPage'))
const CrawlPage = lazy(() => import('./pages/CrawlPage'))
const RankProjectsPage = lazy(() => import('./pages/RankProjectsPage'))
const RankProjectDetailPage = lazy(() => import('./pages/RankProjectDetailPage'))
const RankNewProjectPage = lazy(() => import('./pages/RankNewProjectPage'))
const AiSettingsPage = lazy(() => import('./pages/AiSettingsPage'))
const ToolsPage = lazy(() => import('./pages/ToolsPage'))
const PricingPage = lazy(() => import('./pages/PricingPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const AdminPage = lazy(() => import('./pages/AdminPage'))
const PublicResultPage = lazy(() => import('./pages/PublicResultPage'))
const SnapshotPage = lazy(() => import('./pages/SnapshotPage'))
const UnlockPage = lazy(() => import('./pages/UnlockPage'))
const WebsitesPage = lazy(() => import('./pages/WebsitesPage'))
const NapovedaPage = lazy(() => import('./pages/NapovedaPage'))
const WebsiteStatsPage = lazy(() => import('./pages/WebsiteStatsPage'))
const ContentOptimizerPage = lazy(() => import('./pages/ContentOptimizerPage'))
const GscPage = lazy(() => import('./pages/GscPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const FeaturePage = lazy(() => import('./pages/FeaturePage'))
const ComparisonPage = lazy(() => import('./pages/ComparisonPage'))
const AiVisibilityPage = lazy(() => import('./pages/AiVisibilityPage'))
const AiVisibilityDetailPage = lazy(() => import('./pages/AiVisibilityDetailPage'))
const BacklinksPage = lazy(() => import('./pages/BacklinksPage'))
const ToolLandingPage = lazy(() => import('./pages/ToolLandingPage'))
// Lokalizované segmenty cest (např. /nastroje, /tools, /werkzeuge) — viz lib/localizedUrl
import { allSegments, allPagePaths } from './lib/localizedUrl'
const BlogPage = lazy(() => import('./pages/BlogPage'))
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'))
const KeywordIdeasPage = lazy(() => import('./pages/KeywordIdeasPage'))
const ApiDocsPage = lazy(() => import('./pages/ApiDocsPage'))
const RecentAnalysesPage = lazy(() => import('./pages/RecentAnalysesPage'))
const WebsiteDetailPage = lazy(() => import('./pages/WebsiteDetailPage'))
const OAuthCallbackPage = lazy(() => import('./pages/OAuthCallbackPage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'))

function RouteFallback() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="animate-spin w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full" />
    </div>
  )
}

// Jazyk v cestě (/fr, /es, /nl, /de…) — hreflang na ně odkazuje. Nastaví jazyk a vykreslí
// domovskou stránku. Neznámý segment → 404. Statické routy (/tools…) mají přednost.
function LangHome() {
  const { lng } = useParams()
  const ok = !!lng && LANGUAGES.some((l) => l.code === lng)
  useEffect(() => {
    if (ok && lng && i18n.language !== lng) i18n.changeLanguage(lng)
  }, [ok, lng])
  return ok ? <HomePage /> : <NotFoundPage />
}

// Hlubší cesta s jazykem (/fr/pricing, /es/tools…) — nastaví jazyk a přesměruje na cestu bez prefixu.
function LangDeep() {
  const params = useParams()
  const lng = params.lng
  const rest = params['*'] || ''
  const ok = !!lng && LANGUAGES.some((l) => l.code === lng)
  useEffect(() => {
    if (ok && lng && i18n.language !== lng) i18n.changeLanguage(lng)
  }, [ok, lng])
  if (!ok) return <NotFoundPage />
  return <Navigate to={'/' + rest} replace />
}

export default function App() {
  // Zachyť referral kód z ?ref=… a ulož ho do registrace (přežije až do registrace).
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref')
    if (ref && /^[A-Za-z0-9-]{4,32}$/.test(ref)) {
      localStorage.setItem('ref_code', ref.toUpperCase())
    }
  }, [])

  // Odložená konverze registrace (přes OAuth, který dělá full reload).
  useEffect(() => {
    flushPendingSeznamConversion()
    flushPendingGoogleAdsConversion()
  }, [])

  return (
    <KnowledgeProvider>
    <ScrollToTop />
    <SeznamRetargeting />
    <GoogleAnalytics />
    <VersionGuard />
    <Toaster />
    <IdleTimer />
    <SessionEndedScreen />
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/tools" element={<ToolsPage />} />
        {/* Landing nástrojů — lokalizovaný segment per jazyk (/nastroje, /tools, /werkzeuge…) + starý český. */}
        {allSegments('tools').map((s) => (
          <Route key={s} path={`/${s}/:slug`} element={<ToolLandingPage />} />
        ))}
        <Route path="/blog" element={<BlogPage />} />
        {allSegments('blog').map((s) => (
          <Route key={`blog-${s}`} path={`/${s}/:slug`} element={<BlogPostPage />} />
        ))}
        {allPagePaths('keywordIdeas').map((p) => <Route key={p} path={p} element={<KeywordIdeasPage />} />)}
        <Route path="/api-docs" element={<ApiDocsPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        {/* Nápověda — lokalizovaný segment (/napoveda, /help, /hilfe…), index i detail. */}
        {allSegments('help').map((s) => (
          <Route key={`help-${s}`} path={`/${s}`} element={<NapovedaPage />} />
        ))}
        {allSegments('help').map((s) => (
          <Route key={`help-id-${s}`} path={`/${s}/:id`} element={<NapovedaPage />} />
        ))}
        <Route path="/content-optimizer" element={<ContentOptimizerPage />} />
        <Route path="/analysis/:id" element={<AnalysisResultPage />} />
        <Route path="/results/:domain" element={<PublicResultPage />} />
        <Route path="/web/:domain" element={<WebsiteDetailPage />} />
        <Route path="/s/:token" element={<SnapshotPage />} />
        <Route path="/odemknout" element={<UnlockPage />} />
        {/* Statické stránky: česká cesta + anglická pro ostatní jazyky (pathSegments.json → pages). */}
        {allPagePaths('privacy').map((p) => <Route key={p} path={p} element={<PrivacyPage />} />)}
        {allPagePaths('terms').map((p) => <Route key={p} path={p} element={<TermsPage />} />)}
        {allPagePaths('contact').map((p) => <Route key={p} path={p} element={<ContactPage />} />)}
        {allSegments('features').map((s) => (
          <Route key={`feat-${s}`} path={`/${s}/:slug`} element={<FeaturePage />} />
        ))}
        {allPagePaths('comparison').map((p) => <Route key={p} path={p} element={<ComparisonPage />} />)}
        {allPagePaths('recentAnalyses').map((p) => <Route key={p} path={p} element={<RecentAnalysesPage />} />)}
        <Route path="/oauth" element={<OAuthCallbackPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        {/* Jazyk v cestě (/fr, /es…) — hreflang na ně odkazuje. Statické routy mají přednost. */}
        <Route path="/:lng" element={<LangHome />} />
        <Route path="/:lng/*" element={<LangDeep />} />
        {/* Catch-all 404 — ať neznámá URL nehází bílou stránku */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route element={<Layout />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/websites" element={<WebsitesPage />} />
          <Route path="/statistika/:domain" element={<WebsiteStatsPage />} />
          <Route path="/gsc" element={<GscPage />} />
          <Route path="/crawl" element={<CrawlPage />} />
          <Route path="/rank" element={<RankProjectsPage />} />
          <Route path="/rank/new" element={<RankNewProjectPage />} />
          <Route path="/rank/:id" element={<RankProjectDetailPage />} />
          <Route path="/ai-viditelnost" element={<AiVisibilityPage />} />
          <Route path="/ai-viditelnost/:id" element={<AiVisibilityDetailPage />} />
          <Route path="/zpetne-odkazy" element={<BacklinksPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route element={<AdminRoute />}>
            <Route path="/ai-settings" element={<AiSettingsPage />} />
            <Route path="/admin" element={<AdminPage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
    </Suspense>
    </KnowledgeProvider>
  )
}
